/**
 * Stable identifier helpers.
 *
 * Employee ids are derived from the full rota name so that saved rules,
 * aliases and locks survive re-importing the same rota next week without the
 * manager having to re-link anybody.
 */

/** "Alicia Brennan" -> "alicia-brennan". */
export function slugify(value) {
  return String(value ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

/** Employee id from a full name; falls back to a positional id when blank. */
export function employeeIdFromName(name, fallbackIndex = 0) {
  const slug = slugify(name);
  return slug || `employee-${fallbackIndex + 1}`;
}

/**
 * Loosely normalise a name for matching: lowercase, no punctuation, collapsed
 * whitespace. Used when reconciling alias targets and pasted rota names.
 */
export function normaliseName(value) {
  return String(value ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** First word of a name, capitalised - the default display alias. */
export function firstNameOf(value) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  const first = trimmed.split(/\s+/)[0];
  return first.charAt(0).toUpperCase() + first.slice(1);
}

let counter = 0;

/**
 * Short unique id for transient UI objects (toasts, override records).
 * Deliberately not crypto-random: these ids only need to be unique inside one
 * browser session and stable inside one saved document.
 */
export function uid(prefix = 'id') {
  counter += 1;
  return `${prefix}-${counter.toString(36)}-${Math.floor(performance.now() * 1000).toString(36)}`;
}
